import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, cn, Ikon, Iskelet, Kart, MetinAlani, anMetni } from "@koclukpanel/ui";
import { useSudoKapisi } from "./SudoKapisi";
import {
  ApiHatasi,
  bakimAc,
  bakimGecmisi,
  bakimDurumu,
  bakimKapat,
  demoDurumu,
  demoVeriSil,
  demoVeriUret,
  isTetikle,
  kuyrukYenidenDene,
  surumBilgisi,
  teknikKonsolu,
  type DemoSilmeSonucu,
  type DemoUretimSonucu,
  type GocDurumu,
  type KuyrukSagligi,
} from "./api";

/**
 * TEKNIK YONETIM KONSOLU — F-TEC-03/04/08
 *
 * ================================================================
 * BAKIM MODU EN USTTE VE ACIKKEN BAGIRIYOR
 * ================================================================
 * Bakim acikken butun kullanicilar disarida. Bunu bir sekmenin
 * icine gomseydik, acik unutulan bakim ancak birileri arayinca fark
 * edilirdi.
 */

const SAGLIK_ADI: Record<KuyrukSagligi, string> = {
  IYI: "İyi",
  DIKKAT: "Dikkat",
  KRITIK: "Kritik",
  DURMUS: "Durmuş",
};

const SAGLIK_SINIFI: Record<KuyrukSagligi, string> = {
  IYI: "bg-basarili-500/15 text-onay-700",
  DIKKAT: "bg-uyari-500/15 text-uyari-800",
  KRITIK: "bg-hata-500/15 text-hata-700",
  // DURMUS kritikten AYRI renk degil ama ayri metin: ikisi de acil.
  DURMUS: "bg-hata-500/15 text-hata-700",
};

function tarih(iso: string | null): string {
  if (iso === null) return "bilinmiyor";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * SURUM VE SEMA — F-TEC-14
 *
 * ================================================================
 * IKI YON, IKI AYRI CUMLE
 * ================================================================
 * "Bekliyor" ve "dosyasi yok" ayni sey degil ve ayni sey gibi
 * gosterilemez: birincisinde `pnpm db:deploy` calistirilir,
 * ikincisinde goc dosyasinin nerede kaldigi aranir. Tek bir "uyumsuz"
 * rozeti, okuyan kisiye YANLIS isi yaptirirdi.
 */
const GOC_METNI: Record<GocDurumu, { etiket: string; aciklama: string; agir: boolean }> = {
  UYGULANDI: { etiket: "uygulandı", aciklama: "", agir: false },
  BEKLIYOR: {
    etiket: "bekliyor",
    aciklama: "Dosya var, veritabanında yok. `pnpm db:deploy` çalıştırılmalı.",
    agir: true,
  },
  DOSYASI_YOK: {
    etiket: "dosyası yok",
    aciklama:
      "Veritabanında var, repoda yok — şema koddan yeni. Sistem çalışır ama veritabanı sıfırlanınca bu değişiklik kaybolur.",
    agir: true,
  },
  YARIM: {
    etiket: "yarım kaldı",
    aciklama: "Başlamış, bitmemiş. Veritabanı bilinmeyen durumda; üstüne yeni göç uygulamayın.",
    agir: true,
  },
  GERI_ALINDI: { etiket: "geri alındı", aciklama: "Geri alındı olarak işaretli.", agir: true },
};

function Surum() {
  const surum = useQuery({ queryKey: ["teknik-surum"], queryFn: surumBilgisi });
  if (surum.isPending || surum.isError) return null;

  const v = surum.data;
  const sorunlular = v.sema.satirlar.filter((r) => r.durum !== "UYGULANDI");

  return (
    <Kart baslik="Sürüm ve şema">
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-notr-600">Uygulama</dt>
        <dd className="text-notr-800">{v.uygulamaSurumu}</dd>

        <dt className="text-notr-600">Şema</dt>
        <dd className="text-notr-800 font-mono text-xs">
          {v.sema.semaSurumu ?? "hiç göç uygulanmamış"}
        </dd>

        <dt className="text-notr-600">Son dağıtım</dt>
        <dd className="text-notr-800">
          {v.dagitimKaydiVarMi ? (
            anMetni(v.sonDagitimAt!)
          ) : (
            /*
             * "-" ya da "hic dagitim yapilmadi" YAZILMIYOR.
             * Olculmedi ile sifir ayni sey degil: `SYSTEM/DEPLOYED`
             * olayi katalogda var ama henuz kimse yazmiyor.
             */
            <span className="text-notr-600">ölçülmüyor — dağıtım adımı henüz kayıt yazmıyor</span>
          )}
        </dd>

        <dt className="text-notr-600">Göç</dt>
        <dd className="text-notr-800">
          {v.sema.satirlar.length} tanımlı
          {sorunlular.length === 0 ? (
            <span className="text-onay-600"> · hepsi uygulanmış</span>
          ) : (
            <span className="text-hata-500"> · {sorunlular.length} tanesi dikkat istiyor</span>
          )}
        </dd>
      </dl>

      {sorunlular.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {sorunlular.map((r) => (
            <li
              key={r.ad}
              className="border-uyari-200 bg-uyari-50 rounded border px-3 py-2 text-xs"
            >
              <span className="text-uyari-800 font-medium">{GOC_METNI[r.durum].etiket}</span>
              <span className="text-notr-700 ml-2 font-mono break-all">{r.ad}</span>
              <p className="text-notr-600 mt-1">{GOC_METNI[r.durum].aciklama}</p>
            </li>
          ))}
        </ul>
      )}
    </Kart>
  );
}

/**
 * SON SONUCUN DORT HALI — F-TEC-04.
 *
 * `BILINMIYOR` bilerek notr: bir ariza degil, olcumun sinirlari.
 * `HIC_CALISMADI` da uyari rengi DEGIL — tuvalin notu bunu ayrica
 * yaziyor: "Hic calismadi basarisiz DEGIL."
 */
const SON_SONUC_ADI: Record<string, string> = {
  CALISTI: "çalıştı",
  BASARISIZ: "başarısız",
  HIC_CALISMADI: "hiç çalışmadı",
  BILINMIYOR: "bilinmiyor",
};

const SON_SONUC_BICIMI: Record<string, string> = {
  // Belirtec adlari `tokens.css`ten OKUNDU, tahmin edilmedi: basari
  // bandi `onay-*`, `basari-*` diye bir aile YOK ve Tailwind bilinmeyen
  // adi sessizce atardi — rozet zeminsiz cikardi.
  CALISTI: "bg-onay-50 text-onay-700 ring-onay-200",
  BASARISIZ: "bg-hata-50 text-hata-700 ring-hata-200",
  HIC_CALISMADI: "bg-notr-50 text-notr-600 ring-notr-300",
  BILINMIYOR: "bg-notr-50 text-notr-600 ring-notr-300",
};

function sonCalismaMetni(i: {
  sonSonuc: string;
  sonCalismaAt: string | null;
  kayitPenceresiBaslangici: string | null;
}): string {
  if (i.sonCalismaAt !== null) {
    return i.sonSonuc === "BASARISIZ"
      ? `Son çalışma ${tarih(i.sonCalismaAt)} — BAŞARISIZ bitti.`
      : `Son çalışma: ${tarih(i.sonCalismaAt)}`;
  }
  if (i.sonSonuc === "HIC_CALISMADI") {
    return "Hiç çalışmadı — zamanlayıcı henüz hiç açılmamış olabilir. Bu bir arıza değil.";
  }
  /**
   * "Bilinmiyor" tek basina birakilmiyor: NEDEN bilinmedigi yaziliyor.
   * Aksi halde okuyan kisi bunu bir ariza sanip aramaya baslardi.
   */
  return i.kayitPenceresiBaslangici === null
    ? "Son çalışma bilinmiyor — kuyruk kayıtları okunamadı."
    : `Son çalışma bilinmiyor — kayıtlar yalnızca ${tarih(i.kayitPenceresiBaslangici)} tarihine kadar geriye gidiyor.`;
}

export function TeknikKonsol() {
  const qc = useQueryClient();
  const { hataYakala, kapi } = useSudoKapisi();

  const konsol = useQuery({
    queryKey: ["teknik-konsol"],
    queryFn: teknikKonsolu,
    // Kuyruk sayilari hizli degisiyor; konsol acikken tazeleniyor.
    refetchInterval: 15_000,
  });
  const bakim = useQuery({ queryKey: ["bakim-durum"], queryFn: bakimDurumu });

  const yenile = () => {
    void qc.invalidateQueries({ queryKey: ["teknik-konsol"] });
    void qc.invalidateQueries({ queryKey: ["bakim-durum"] });
  };

  const dene = useMutation({
    mutationFn: (k: string) => kuyrukYenidenDene(k),
    onSuccess: yenile,
    onError: hataYakala(() => undefined),
  });
  const tetikle = useMutation({
    mutationFn: (i: string) => isTetikle(i),
    onSuccess: yenile,
    onError: hataYakala(() => undefined),
  });

  if (konsol.isPending) return <Iskelet satir={4} />;
  if (konsol.isError) {
    return (
      <p className="text-hata-500 text-sm">
        {konsol.error instanceof ApiHatasi ? konsol.error.message : "Konsol açılamadı."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {kapi}

      <BakimBolumu durum={bakim.data} onDegisti={yenile} />
      <BakimGecmisi />
      {konsol.data.demoVeriAcik !== false && <DemoVeriBolumu />}

      {/* ---------- KUYRUKLAR ---------- */}
      <section className="flex flex-col gap-2">
        <h3 className="text-notr-800 text-sm font-semibold">Kuyruklar</h3>
        {/*
          "Bekleyen is" tek basina kotu DEGIL ve bunu yazmak sart:
          aksi halde her sabah dolu gorunen denetim kuyrugu paniğe
          yol acar.
        */}
        <p className="text-notr-600 text-xs">
          Bekleyen iş tek başına sorun değildir — işleniyorsa normaldir. Asıl bulgular
          <strong> başarısız</strong> işler (kendiliğinden gitmezler) ve
          <strong> durmuş</strong> kuyruk (bekleyen var, işleyen yok).
        </p>
        {/*
          ================================================================
          SORUNLU KUYRUK KART, SAGLIKLI KUYRUK SATIR — CIZIMDEN
          ================================================================
          `TeknikKonsol.dc.html` bunu iki ayri notla soyluyor:

            "KUYRUKLAR: dort yesil bir kirmizi. Kirmizi KAYBOLMAMALI."
            "DURMUS: satir degil, KART. Digerlerinden farkli katmanda
             durup dort yesil satirin arasinda kaybolmuyor."

          Onceki hâlde BES KUYRUK DA `Kart` idi: durmus olan yalnizca
          rozet rengiyle ayriliyordu. Bes esit agirlikli kartin arasinda
          bir rozet rengi, tarama yapan gozun kacirdigi ilk seydir —
          ustelik bu ekranin varlik sebebi tam olarak o kirmiziyi
          gormek.

          Ayrim RENK DEGIL YAPI: sorunlu kuyruk kendi kartinda ve
          kenarligi var; saglikli olanlar tek satira iniyor. Renk
          korlugunde de okunuyor.
        */}
        {(() => {
          const sorunlu = konsol.data.kuyruklar.filter((k) => k.saglik !== "IYI");
          const saglikli = konsol.data.kuyruklar.filter((k) => k.saglik === "IYI");
          return (
            <>
              {sorunlu.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {sorunlu.map((k) => (
                    <li
                      key={k.ad}
                      className={cn(
                        "rounded-kart bg-notr-0 shadow-yukselen border p-4",
                        k.saglik === "DIKKAT" ? "border-uyari-200" : "border-hata-200",
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="flex items-center gap-2">
                          <Ikon
                            ad={k.saglik === "DIKKAT" ? "uyari" : "hata"}
                            boyut="kucuk"
                            className={k.saglik === "DIKKAT" ? "text-uyari-800" : "text-hata-700"}
                          />
                          <span className="text-notr-900 font-mono text-sm font-semibold">
                            {k.ad}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "rounded-alan px-2.5 py-0.5 text-xs font-medium",
                            SAGLIK_SINIFI[k.saglik],
                          )}
                        >
                          {SAGLIK_ADI[k.saglik]}
                        </span>
                      </div>
                      <p className="text-notr-700 mt-1.5 text-sm">{k.sebep}</p>
                      <p className="text-notr-600 mt-1 text-xs">
                        bekleyen {k.bekleyen} · işleyen {k.isleyen} · başarısız {k.basarisiz} ·
                        gecikmeli {k.gecikmeli}
                      </p>
                      {k.basarisiz > 0 && (
                        <div className="mt-2">
                          <Buton
                            gorunum="ikincil"
                            onClick={() => dene.mutate(k.ad)}
                            disabled={dene.isPending}
                          >
                            Başarısızları yeniden dene
                          </Buton>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {saglikli.length > 0 && (
                /*
                  SAGLIKLI KUYRUKLAR GIZLENMIYOR, SOLUYOR. Gizleseydik
                  "bu kuyruk var mi" sorusu cevapsiz kalirdi ve ekranda
                  yalnizca sorunlular gorunurdu — sorun yokken ekran bos
                  acilirdi. Sifir karti kuralinin aynisi: bakildi demek.
                */
                <ul className="border-notr-200 divide-notr-100 rounded-kart bg-notr-0 divide-y border">
                  {saglikli.map((k) => (
                    <li
                      key={k.ad}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
                    >
                      <span className="text-notr-700 font-mono text-sm">{k.ad}</span>
                      <span className="text-notr-600 flex items-center gap-3 text-xs">
                        <span>
                          bekleyen {k.bekleyen} · işleyen {k.isleyen}
                        </span>
                        <span className="text-onay-700 font-medium">{SAGLIK_ADI[k.saglik]}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          );
        })()}
        {dene.isSuccess && (
          <p className="text-notr-600 text-xs">
            {dene.data.denenen} iş yeniden denendi
            {dene.data.kalan > 0 && ` · ${dene.data.kalan} başarısız iş hâlâ duruyor`}.
          </p>
        )}
      </section>

      {/* ---------- ZAMANLANMIŞ İŞLER ---------- */}
      <section className="flex flex-col gap-2">
        <h3 className="text-notr-800 text-sm font-semibold">Zamanlanmış işler</h3>
        <ul className="flex flex-col gap-2">
          {konsol.data.isler.map((i) => (
            <Kart key={i.ad} olarak="li">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-notr-800 font-mono text-sm">{i.ad}</p>
                  <p className="text-notr-600 text-xs">{i.gereksinim}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <code className="bg-notr-50 text-notr-700 rounded px-2 py-0.5 text-xs">
                    {i.desen}
                  </code>
                  <span
                    className={cn(
                      "rounded px-2 py-0.5 text-xs ring-1 ring-inset",
                      SON_SONUC_BICIMI[i.sonSonuc],
                    )}
                  >
                    {SON_SONUC_ADI[i.sonSonuc]}
                  </span>
                  <Buton
                    gorunum="sessiz"
                    onClick={() => tetikle.mutate(i.ad)}
                    disabled={tetikle.isPending}
                  >
                    Şimdi çalıştır
                  </Buton>
                </div>
              </div>
              <p className={cn("mt-1 text-xs", i.gecikmis ? "text-hata-500" : "text-notr-600")}>
                {/*
                  Sonraki calisma bilinmiyorsa "bilinmiyor" yaziyor.
                  Bos birakmak, zamanlayicinin calistigini sandirirdi.
                */}
                {i.sonrakiCalismaAt === null
                  ? "Sıradaki çalışma bilinmiyor — zamanlayıcı (pnpm scheduler) açık olmayabilir."
                  : i.gecikmis
                    ? `GECİKMİŞ — planlanan zaman ${tarih(i.sonrakiCalismaAt)} geçti.`
                    : `Sıradaki çalışma: ${tarih(i.sonrakiCalismaAt)}`}
              </p>
              {/*
                SON CALISMA AYRI SATIRDA.
                "Hic calismadi" basarisiz DEGIL — zamanlayici hic acilmamis
                olabilir. Ikisini ayni renge koymak, kurulum eksikligini
                ariza gibi gosterirdi.
              */}
              <p className="text-notr-600 mt-0.5 text-xs">{sonCalismaMetni(i)}</p>
            </Kart>
          ))}
        </ul>
      </section>

      {/* ---------- YAZILMAMIŞ İŞLER ---------- */}
      {konsol.data.yazilmamisIsler.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-notr-800 text-sm font-semibold">Planlanmış ama yazılmamış</h3>
          {/*
            Bunlar ZAMANLANMIYOR ve sebebi yaziyor. Listede
            gostermeseydik "bu is neden yok" sorusu, birinin onlari
            zamanlamasiyla cevaplanirdi — isleyicileri olmadigi icin
            de her gun sessizce basarili gorunurlerdi.
          */}
          <p className="text-notr-600 text-xs">
            Bu işler <strong>kuyruğa düşmüyor</strong>. İşleyicileri yazılmadan zamanlansalardı her
            gün sessizce “başarılı” kapanır ve özellik çalışıyor görünürdü.
          </p>
          <ul className="flex flex-col gap-2">
            {konsol.data.yazilmamisIsler.map((y) => (
              <li
                key={y.name}
                className="rounded-kart border-notr-200 shadow-kart bg-notr-50 border p-3 text-sm"
              >
                <span className="text-notr-800 font-mono text-xs">{y.name}</span>
                <p className="text-notr-600 mt-1 text-xs">{y.requirement}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function BakimBolumu({
  durum,
  onDegisti,
}: {
  durum: { acik: boolean; baslik: string | null; mesaj: string | null } | undefined;
  onDegisti: () => void;
}) {
  const { hataYakala, kapi } = useSudoKapisi();
  const [baslik, setBaslik] = useState("");
  const [mesaj, setMesaj] = useState("");

  const ac = useMutation({
    mutationFn: () => bakimAc({ baslik: baslik.trim(), mesaj: mesaj.trim() }),
    onSuccess: () => {
      setBaslik("");
      setMesaj("");
      onDegisti();
    },
    onError: hataYakala(() => undefined),
  });
  const kapat = useMutation({
    mutationFn: () => bakimKapat(),
    onSuccess: onDegisti,
    onError: hataYakala(() => undefined),
  });

  if (durum?.acik) {
    return (
      <>
        {kapi}
        <section
          role="alert"
          className="rounded-kart shadow-kart border-hata-500/40 bg-hata-500/10 border p-4"
        >
          <h3 className="text-hata-500 text-sm font-semibold">BAKIM MODU AÇIK</h3>
          <p className="text-notr-800 mt-1 text-sm">
            <strong>{durum.baslik}</strong> — {durum.mesaj}
          </p>
          {/*
            Kimlerin hâlâ girebildigi YAZIYOR: bakimi acan kisi
            "kimse giremiyor" sanip paniklemesin.
          */}
          <p className="text-notr-600 mt-1 text-xs">
            Şu an yalnızca bakımı yönetebilenler ve giriş ekranı erişilebilir. Bakımı kapatana kadar
            diğer herkes 503 alıyor.
          </p>
          <div className="mt-3">
            <Buton onClick={() => kapat.mutate()} disabled={kapat.isPending}>
              {kapat.isPending ? "Kapatılıyor…" : "Bakımı kapat"}
            </Buton>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {kapi}
      <Kart baslik="Bakım modu">
        <p className="text-notr-600 mt-1 text-xs">
          Açıldığında giriş ekranı ve bakım yönetimi dışında her şey kapanır. Mesaj kullanıcıya{" "}
          <strong>aynen</strong> gösterilir — “sistem kapalı” demek yerine ne zaman açılacağını
          yazın.
        </p>
        <div className="mt-3 flex flex-col gap-2">
          <Alan
            etiket="Başlık"
            value={baslik}
            onChange={(e) => setBaslik(e.target.value)}
            placeholder="Örn. Sürüm geçişi"
          />
          <MetinAlani
            etiket="Kullanıcıya gösterilecek mesaj (en az 10 karakter)"
            rows={2}
            value={mesaj}
            onChange={(e) => setMesaj(e.target.value)}
          />
          <div>
            <Buton
              onClick={() => ac.mutate()}
              disabled={ac.isPending || baslik.trim().length < 3 || mesaj.trim().length < 10}
            >
              {ac.isPending ? "Açılıyor…" : "Bakım modunu aç"}
            </Buton>
          </div>
          {ac.isError && (
            <p className="text-hata-500 text-xs">
              {ac.error instanceof ApiHatasi ? ac.error.message : "Bakım açılamadı."}
            </p>
          )}
        </div>
      </Kart>

      <Surum />
    </>
  );
}

// =====================================================================
// DEMO VERİSİ — F-TEC-15
// =====================================================================

/**
 * ================================================================
 * BÖLÜM VARSA ÖZELLİK AÇIK DEMEKTİR
 * ================================================================
 * Uçlar `DEMO_VERI=true` olmadan **hiç tanımlanmıyor** (sunucu
 * denetleyiciyi kaydetmiyor). Sorgu 404 dönerse bölüm HİÇ ÇİZİLMİYOR:
 * kapalı bir özelliği "kapalı" diye göstermek, açılabileceği izlenimi
 * verir ve teknik yöneticiyi olmayan bir ayarı aramaya iter.
 *
 * ================================================================
 * BU EKRAN BİR KARARIN GERİ ALINMASIYLA GELDİ
 * ================================================================
 * 03.08.2026'da "üretim panelden bir düğmeye bağlanmıyor" diye karara
 * bağlanmıştı; gerekçe "olmayan bir uca yanlışlıkla istek atılamaz"
 * idi. Karar 02.09.2026'da değişti ve o itiraz dört bağımsız kapıyla
 * karşılandı — biri de bu bölümün var olup olmaması.
 */
function DemoVeriBolumu() {
  const qc = useQueryClient();
  const [adet, setAdet] = useState("25");
  const [silOnay, setSilOnay] = useState(false);
  const [uretim, setUretim] = useState<DemoUretimSonucu | null>(null);
  const [silme, setSilme] = useState<DemoSilmeSonucu | null>(null);

  const durum = useQuery({ queryKey: ["demo-veri"], queryFn: demoDurumu, retry: false });

  const yenile = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["demo-veri"] });
  };

  const uret = useMutation({
    mutationFn: () => demoVeriUret(Number(adet)),
    onSuccess: async (s) => {
      setUretim(s);
      setSilme(null);
      await yenile();
    },
  });

  const sil = useMutation({
    mutationFn: demoVeriSil,
    onSuccess: async (s) => {
      setSilme(s);
      setUretim(null);
      setSilOnay(false);
      await yenile();
    },
  });

  // Bayrak kapali ya da izin yok -> bolum HIC yok.
  if (durum.isPending || durum.isError) return null;

  const engelli = durum.data.engel !== null;
  const sayi = Number(adet);
  const gecersizAdet = !Number.isInteger(sayi) || sayi < 0 || sayi > 500;

  return (
    <Kart
      baslik="Demo verisi"
      aciklama="Sistemi yayına almadan önce bütün alanlarıyla gezip hatayı bulmak için üretilmiş sahte kayıtlar. Gerçek öğrenci verisi hiçbir koşulda kopyalanmaz." /* G-VER-07 */
    >
      <div className="flex flex-col gap-3">
        <p className="text-notr-700 text-sm">
          Veritabanı: <span className="font-mono">{durum.data.veritabani ?? "bilinmiyor"}</span> ·
          bu veritabanında <strong>{durum.data.uretilmisKullanici}</strong> üretilmiş kullanıcı var.
        </p>

        {/*
          ENGEL VARSA SEBEBİYLE YAZILIYOR. "Düğme çalışmıyor" demek,
          teknik yöneticiyi hatayı kendinde aramaya iter; oysa sebep
          bilinçli bir korumadır.
        */}
        {engelli && (
          <p className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 border px-3 py-2 text-sm">
            <strong>Üretim ve silme kapalı.</strong> {durum.data.engel}
          </p>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div className="w-36">
            <Alan
              etiket="Öğrenci sayısı"
              type="number"
              min={0}
              max={500}
              value={adet}
              onChange={(e) => setAdet(e.target.value)}
              yardim="0 = yeni öğrenci üretme, yalnızca operasyon katmanını doldur."
            />
          </div>
          <Buton
            yukleniyor={uret.isPending}
            disabled={engelli || gecersizAdet}
            onClick={() => uret.mutate()}
          >
            Demo verisi üret
          </Buton>
          <Buton
            gorunum="ikincil"
            disabled={engelli || durum.data.uretilmisKullanici === 0}
            onClick={() => setSilOnay((a) => !a)}
          >
            {silOnay ? "Vazgeç" : "Demo verisini sil"}
          </Buton>
        </div>

        {silOnay && (
          <div className="rounded-kart shadow-kart border-uyari-200 bg-uyari-50 flex flex-col gap-2 border p-3">
            <p className="text-uyari-800 text-sm">
              Bu veritabanındaki <strong>{durum.data.uretilmisKullanici}</strong> üretilmiş
              kullanıcı ve bağlı bütün kayıtları silinecek. Gerçek kayıtlara dokunulmaz — yalnızca{" "}
              <span className="font-mono">@test.gecersiz</span> işaretli olanlar.
            </p>
            {/*
              TAM SİLİNEMEYECEĞİ ÖNCEDEN SÖYLENİYOR. Sonuçta çıkan
              "2 kullanıcı silinemedi" satırı sürpriz olmasın: sebebi
              bilinçli bir koruma ve kullanıcı bunu baştan bilmeli.
            */}
            <p className="text-uyari-800 text-xs">
              Korumalı not okumuş ya da aktif süresi ölçülmüş kullanıcılar{" "}
              <strong>silinemez</strong> — değiştirilemez kayıtlara bağlılar. Hangileri olduğu
              sonuçta yazılacak. Tamamen temizlemek için <code>pnpm db:reset</code>.
            </p>
            <div>
              <Buton yukleniyor={sil.isPending} onClick={() => sil.mutate()}>
                Evet, sil
              </Buton>
            </div>
          </div>
        )}

        {uretim && (
          <div className="rounded-alan border-onay-200 bg-onay-50 text-onay-700 border p-3 text-sm">
            <p className="font-medium">{uretim.yazilan} öğrenci yazıldı.</p>
            <ul className="mt-1 flex flex-wrap gap-x-3 text-xs">
              {Object.entries(uretim.operasyon).map(([ad, s]) => (
                <li key={ad}>
                  {s} {ad}
                </li>
              ))}
            </ul>
          </div>
        )}

        {silme && (
          <div className="flex flex-col gap-2">
            <div className="rounded-alan border-onay-200 bg-onay-50 text-onay-700 border p-3 text-sm">
              <p className="font-medium">
                {Object.values(silme.silinen).reduce((a, b) => a + b, 0)} kayıt silindi.
              </p>
              <ul className="mt-1 flex flex-wrap gap-x-3 text-xs">
                {Object.entries(silme.silinen).map(([ad, s]) => (
                  <li key={ad}>
                    {s} {ad}
                  </li>
                ))}
              </ul>
            </div>

            {/*
              SİLİNEMEYENLER SESSİZ KALMIYOR. Sessizce eksik silmek,
              kullanıcının temizlendiğini sanmasına yol açardı — ve o
              sanı, canlıya geçerken sahte kayıt bırakma riskidir.
            */}
            {silme.silinemeyen.length > 0 && (
              <div className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 border p-3 text-sm">
                <p className="font-medium">{silme.silinemeyen.length} kullanıcı silinemedi:</p>
                <ul className="mt-1 flex flex-col gap-0.5 text-xs">
                  {silme.silinemeyen.map((s) => (
                    <li key={s.eposta}>
                      <span className="font-mono">{s.eposta}</span> — {s.sebep}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs">
                  Bu bilinçli bir koruma: denetim izleri silinemez{/* F-LOG-17 */}. Tamamen
                  temizlemek için <code>pnpm db:reset</code>.
                </p>
              </div>
            )}
          </div>
        )}

        {(uret.isError || sil.isError) && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {(uret.error ?? sil.error) instanceof ApiHatasi
              ? ((uret.error ?? sil.error) as ApiHatasi).message
              : "İşlem tamamlanamadı."}
          </p>
        )}
      </div>
    </Kart>
  );
}

/**
 * GEÇMİŞ BAKIM PENCERELERİ — "sistem ne zaman kapalıydı"
 *
 * `GET /bakim/gecmis` yazılıydı, hiçbir ekran çağırmıyordu. Bakım
 * AÇILIP KAPATILABİLİYOR ama ne zaman kapalı olduğunun kaydı
 * görünmüyordu — "o gün giremedim" diyen kullanıcıya bakılacak yer
 * yoktu.
 *
 * Son 30 pencere dönüyor; ancak açıldığında isteniyor.
 */
function BakimGecmisi() {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["bakim-gecmisi"],
    queryFn: bakimGecmisi,
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Geçmiş bakım pencereleri
        </Buton>
      </div>
    );
  }

  return (
    <section className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-2 border p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Geçmiş bakım pencereleri</h3>
        <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
          Kapat
        </Buton>
      </div>

      {isPending ? (
        <Iskelet satir={3} />
      ) : isError ? (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {error instanceof ApiHatasi ? error.message : "Geçmiş alınamadı."}
        </p>
      ) : data.length === 0 ? (
        <p className="text-notr-600 text-sm">
          Hiç bakım penceresi açılmamış. Sistem bugüne kadar planlı olarak kapatılmadı.
        </p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {data.map((b) => (
            <li key={b.id} className="rounded-alan bg-notr-50 p-2">
              <p className="text-notr-900 font-medium">
                {b.title}
                {b.isActive && <span className="text-uyari-600 ml-2 text-xs">AÇIK</span>}
              </p>
              <p className="text-notr-600 mt-0.5 text-xs">
                {anMetni(b.startsAt)} —{" "}
                {/*
                  `endsAt === null` = bakim HALA ACIK ya da kapanis
                  saati yazilmamis. "Bitiş yok" demek, suresiz bir
                  kapanma gibi okunurdu.
                */}
                {b.endsAt === null ? (
                  <span className="text-uyari-600">bitiş kaydedilmedi</span>
                ) : (
                  anMetni(b.endsAt)
                )}
              </p>
              {b.message !== null && (
                <p className="text-notr-700 mt-1 text-xs whitespace-pre-wrap">{b.message}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
